import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCamera, FiRotateCw, FiZoomIn, FiZoomOut, FiMaximize, FiEye, FiPlay } = FiIcons;

function VirtualShowroom() {
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [currentView, setCurrentView] = useState('exterior');
  const [currentAngle, setCurrentAngle] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [tourActive, setTourActive] = useState(false);

  const vehicles = [
    {
      id: 1,
      make: 'Audi',
      model: 'Q5 Quattro',
      year: 2023,
      exteriorViews: [
        'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1617788138017-80ad40651399?w=800&h=600&fit=crop'
      ],
      interiorViews: [
        'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1502877338535-766e1452684a?w=800&h=600&fit=crop'
      ],
      features: ['360° View', 'Interior Tour', 'AR Integration', 'Virtual Test Drive']
    }
  ];

  const viewTypes = [
    { id: 'exterior', label: 'Exterior', icon: FiCamera },
    { id: 'interior', label: 'Interior', icon: FiEye },
    { id: 'engine', label: 'Engine', icon: FiRotateCw },
    { id: 'trunk', label: 'Trunk', icon: FiMaximize }
  ];

  const startVirtualTour = () => {
    setTourActive(true);
    // Simulate guided tour
    const tourSteps = ['exterior', 'interior', 'engine', 'trunk'];
    let currentStep = 0;
    
    const tourInterval = setInterval(() => {
      if (currentStep < tourSteps.length) {
        setCurrentView(tourSteps[currentStep]);
        currentStep++;
      } else {
        setTourActive(false);
        clearInterval(tourInterval);
      }
    }, 3000);
  };

  const currentImages = selectedVehicle ? 
    (currentView === 'interior' ? selectedVehicle.interiorViews : selectedVehicle.exteriorViews) : [];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 to-blue-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-white mb-4">
            Halifax Virtual Showroom
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Explore every detail of our vehicles with immersive 360° views, virtual tours, and AR integration - all from Halifax comfort.
          </p>
        </motion.div>

        {!selectedVehicle ? (
          <div className="grid md:grid-cols-3 gap-8">
            {vehicles.map((vehicle, index) => (
              <motion.div
                key={vehicle.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover-lift"
              >
                <img
                  src={vehicle.exteriorViews[0]}
                  alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
                <h3 className="text-xl font-bold text-white mb-2">
                  {vehicle.year} {vehicle.make} {vehicle.model}
                </h3>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {vehicle.features.map((feature, idx) => (
                    <span key={idx} className="text-xs bg-blue-600/30 text-blue-200 px-2 py-1 rounded">
                      {feature}
                    </span>
                  ))}
                </div>
                <button
                  onClick={() => setSelectedVehicle(vehicle)}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiCamera} className="w-4 h-4" />
                  <span>Enter Virtual Showroom</span>
                </button>
              </motion.div>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-black/50 backdrop-blur-lg rounded-xl overflow-hidden"
          >
            {/* Header */}
            <div className="p-6 border-b border-white/10">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold text-white">
                  {selectedVehicle.year} {selectedVehicle.make} {selectedVehicle.model}
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={startVirtualTour}
                    disabled={tourActive}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
                  >
                    <SafeIcon icon={FiPlay} className="w-4 h-4" />
                    <span>{tourActive ? 'Tour Active...' : 'Start Guided Tour'}</span>
                  </button>
                  <button
                    onClick={() => setSelectedVehicle(null)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Exit Showroom
                  </button>
                </div>
              </div>
            </div>

            {/* Main Viewer */}
            <div className="relative">
              <div className={`${isFullscreen ? 'fixed inset-0 z-50 bg-black' : 'h-96'} flex items-center justify-center`}>
                <AnimatePresence mode="wait">
                  <motion.img
                    key={`${currentView}-${currentAngle}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                    src={currentImages[currentAngle] || selectedVehicle.exteriorViews[0]}
                    alt="Vehicle view"
                    className="max-w-full max-h-full object-contain"
                  />
                </AnimatePresence>
                
                {/* Fullscreen toggle */}
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="absolute top-4 right-4 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                >
                  <SafeIcon icon={FiMaximize} className="w-5 h-5" />
                </button>
              </div>

              {/* Controls */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center space-x-4 bg-black/70 rounded-full px-6 py-3">
                <button
                  onClick={() => setCurrentAngle(Math.max(0, currentAngle - 1))}
                  disabled={currentAngle === 0}
                  className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors disabled:opacity-50"
                >
                  <SafeIcon icon={FiRotateCw} className="w-5 h-5 transform rotate-180" />
                </button>
                
                <span className="text-white text-sm">
                  {currentAngle + 1} / {currentImages.length}
                </span>
                
                <button
                  onClick={() => setCurrentAngle(Math.min(currentImages.length - 1, currentAngle + 1))}
                  disabled={currentAngle === currentImages.length - 1}
                  className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors disabled:opacity-50"
                >
                  <SafeIcon icon={FiRotateCw} className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* View Selector */}
            <div className="p-6">
              <div className="flex justify-center space-x-4 mb-6">
                {viewTypes.map((view) => (
                  <button
                    key={view.id}
                    onClick={() => {
                      setCurrentView(view.id);
                      setCurrentAngle(0);
                    }}
                    className={`px-6 py-3 rounded-lg transition-all flex items-center space-x-2 ${
                      currentView === view.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-white/10 text-gray-300 hover:bg-white/20'
                    }`}
                  >
                    <SafeIcon icon={view.icon} className="w-4 h-4" />
                    <span>{view.label}</span>
                  </button>
                ))}
              </div>

              {/* AR Integration */}
              <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-lg p-6 border border-purple-500/30">
                <h4 className="text-lg font-semibold text-white mb-2">Halifax AR Experience</h4>
                <p className="text-gray-300 text-sm mb-4">
                  Use your phone to see this vehicle in your Halifax driveway with augmented reality.
                </p>
                <div className="flex space-x-3">
                  <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                    Launch AR View
                  </button>
                  <button className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors">
                    Share AR Link
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}

export default VirtualShowroom;